/**
 * The browser-storage keys that belong to a signed-in person, and the wipe
 * that removes them when the session ends (M11 P2, "leftovers on the device").
 *
 * localStorage outlives a sign-out, and in the app it is the WebView's, so a
 * key named after a user id told the next person on a shared phone or browser
 * who used it before them. Every key the app writes was classified:
 *
 *   wiped here (names a user, or holds personal data):
 *     app_feedback_last_shown:<user id>   the feedback gate (appFeedbackGate.ts)
 *     helper-onboarding-step:<user id>    the helper wizard's step; the server's
 *                                         profiles.onboarding_step still resumes it
 *     offplatform_modal_dismissed_<id>    the chat's off-platform notice, keyed by
 *                                         the OTHER person's id (ChatInterface)
 *     adminResetThrottle                  emails that asked for an admin reset
 *     th_onboarding_draft / _step         written by the deleted HelperOnboardingWizard
 *                                         (referees' phones); swept in case a build
 *                                         ever left them
 *     session: helper-job-history:<id>    the helper's job-history view (useSessionState)
 *     session: funnel_session_identity    the user id the funnel session belongs to,
 *              funnel_session_id          and that session (funnelSession.ts)
 *
 *   kept (the device's, not the person's):
 *     th-theme, th.locale, cookie-consent, th_remember_me, table-density*,
 *     admin_auto_refresh_ms, admin_read_notifications (fixed alert ids),
 *     th-precise-location-consent (the device's notice), version-watchdog,
 *     booking_flow_id, kanfy.shell.role, trustedHelpers_demoRole, uat_* (the
 *     UAT harness, which has its own sign-in and sign-out),
 *     session: kanfy.whatsappLinkToken (a 10-minute WhatsApp linking ticket for
 *     this tab only; it names no account, approving it still needs a fresh
 *     sign-in and a click, and it must survive the sign-in-again step).
 *
 *   wiped elsewhere: Supabase's auth keys and th-country-override (useAuth),
 *     the booking draft and every tab's operation ids (purgeBookingDraft, on
 *     every SIGNED_OUT and the Sign out button, and every native session
 *     end), the secure store and its outboxes (sessionWipe.ts).
 *
 * A new key that names a user or holds personal data belongs in this list;
 * src/test/perUserBrowserStorage.test.ts lists every key the app writes.
 */
import { logger } from '@/lib/logger';

/** localStorage keys that start with these belong to one person. */
export const PER_USER_LOCAL_PREFIXES = [
  'app_feedback_last_shown:',
  'helper-onboarding-step:',
  'offplatform_modal_dismissed_',
] as const;

/** Whole localStorage keys holding personal data. */
export const PER_USER_LOCAL_KEYS = ['adminResetThrottle', 'th_onboarding_draft', 'th_onboarding_step'] as const;

/** sessionStorage keys that start with these belong to one person. */
export const PER_USER_SESSION_PREFIXES = ['helper-job-history:'] as const;

/** Whole sessionStorage keys naming a person. */
export const PER_USER_SESSION_KEYS = ['funnel_session_id', 'funnel_session_identity'] as const;

function sweep(storage: Storage, prefixes: readonly string[], keys: readonly string[]): void {
  const doomed: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k !== null && (keys.includes(k) || prefixes.some((p) => k.startsWith(p)))) doomed.push(k);
  }
  for (const k of doomed) storage.removeItem(k);
}

function storageOrNull(name: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window[name];
  } catch {
    return null; // a blocked store throws on access
  }
}

/** Removes every per-user key from localStorage and sessionStorage. Never throws. */
export function wipePerUserBrowserStorage(): void {
  const local = storageOrNull('localStorage');
  const session = storageOrNull('sessionStorage');
  try {
    if (local) sweep(local, PER_USER_LOCAL_PREFIXES, PER_USER_LOCAL_KEYS);
  } catch (e) {
    logger.warn('[per-user-storage] localStorage could not be swept', e);
  }
  try {
    if (session) sweep(session, PER_USER_SESSION_PREFIXES, PER_USER_SESSION_KEYS);
  } catch (e) {
    logger.warn('[per-user-storage] sessionStorage could not be swept', e);
  }
}
